import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "../../../AuthContext";
import { FACTORY_SYMBOL_CODES as SYMBOL_CODES } from "utils/symbols";
import { COLORS } from "theme/tokens";

const RISK_LABELS = { LOW: "低風險", MID: "中風險", HIGH: "高風險" };
const RISK_COLORS = { LOW: "#2196f3", MID: COLORS.warning, HIGH: COLORS.error };
const FREQ_LABELS = { HIGH_FREQ: "M15 高頻", LOW_FREQ: "H1 低頻" };
const STATUS_MAP = {
  WAITING:  { label: "等待中", color: COLORS.warning },
  PROMOTED: { label: "已晉升", color: COLORS.safe },
  RETIRED:  { label: "已退役", color: COLORS.grayDark },
};

const fmtDate = (d) => d ? new Date(d).toLocaleString("zh-TW", { hour12: false }) : "—";
const fmtN = (v, d = 1) => v != null ? Number(v).toFixed(d) : "—";

function KpiBadge({ kpi }) {
  if (!kpi) return <span style={{ color: COLORS.grayMedian }}>—</span>;
  return (
    <span style={{ fontSize: 12 }}>
      回報 <b style={{ color: kpi.returnRatePct >= 0 ? COLORS.safe : COLORS.error }}>{fmtN(kpi.returnRatePct)}%</b>
      {" · "}MDD <b>{fmtN(kpi.maxDrawdownPct)}%</b>
      {" · "}PF <b>{fmtN(kpi.profitFactor, 2)}</b>
      {" · "}{kpi.trades}筆
    </span>
  );
}

export default function BankIncubatorPage() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState({});
  const [retiring, setRetiring] = useState({});
  const [reviewingAll, setReviewingAll] = useState(false);
  const [filterStatus, setFilterStatus] = useState("WAITING");
  const [filterSymbol, setFilterSymbol] = useState("");

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterSymbol) params.set("symbol", filterSymbol);
      const res = await fetch(`/api/admin/bank-incubator?${params}`, { headers });
      const json = await res.json();
      if (json.success) setEntries(json.data);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, filterSymbol]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (id) => {
    setReviewing(p => ({ ...p, [id]: true }));
    try {
      const res = await fetch(`/api/admin/bank-incubator/review/${id}`, { method: "POST", headers });
      const json = await res.json();
      if (json.success) {
        alert(json.passed ? "🏆 晉升成功！已移入精英池" : `⏳ 繼續等待。回報: ${fmtN(json.reviewKpi?.returnRatePct)}%`);
        load();
      } else {
        alert(`錯誤: ${json.message}`);
      }
    } finally {
      setReviewing(p => ({ ...p, [id]: false }));
    }
  };

  const handleRetire = async (id) => {
    if (!window.confirm("確定退役這個策略種子？")) return;
    setRetiring(p => ({ ...p, [id]: true }));
    try {
      await fetch(`/api/admin/bank-incubator/retire/${id}`, { method: "POST", headers });
      load();
    } finally {
      setRetiring(p => ({ ...p, [id]: false }));
    }
  };

  const handleReviewAll = async () => {
    if (!window.confirm(`批量重評所有 WAITING 策略？這可能需要較長時間。`)) return;
    setReviewingAll(true);
    try {
      const res = await fetch("/api/admin/bank-incubator/review-all", { method: "POST", headers });
      const json = await res.json();
      if (json.success) {
        const promoted = json.results.filter(r => r.passed).length;
        alert(`批量重評完成！共 ${json.results.length} 筆，晉升 ${promoted} 筆。`);
        load();
      }
    } finally {
      setReviewingAll(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">🌱 策略孵化池</MDTypography>
            <MDTypography variant="body2" color="text">
              訓練期通過但 OOS 落榜的潛力種子，等待市場條件轉變後重新評估
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient" color="warning"
            onClick={handleReviewAll} disabled={reviewingAll}
            startIcon={reviewingAll ? <CircularProgress size={16} color="inherit" /> : <Icon>refresh</Icon>}
          >
            批量重評所有等待中
          </MDButton>
        </MDBox>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 3 }}>
          <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>狀態</InputLabel>
              <Select value={filterStatus} label="狀態" onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="WAITING">等待中</MenuItem>
                <MenuItem value="PROMOTED">已晉升</MenuItem>
                <MenuItem value="RETIRED">已退役</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>幣別</InputLabel>
              <Select value={filterSymbol} label="幣別" onChange={e => setFilterSymbol(e.target.value)}>
                <MenuItem value="">全部</MenuItem>
                {SYMBOL_CODES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={load} startIcon={<Icon>search</Icon>}>
              篩選
            </Button>
            <MDTypography variant="caption" color="text" sx={{ ml: "auto" }}>
              共 {entries.length} 筆
            </MDTypography>
          </MDBox>
        </Card>

        {/* Table */}
        <Card>
          <MDBox p={2}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
            ) : entries.length === 0 ? (
              <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 4 }}>
                孵化池目前是空的
              </MDTypography>
            ) : (
              <MDBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #eee" }}>
                      {["幣別","風險","頻率","狀態","訓練 KPI","OOS 失敗 KPI","失敗原因","最後複評","複評次數","加入時間","操作"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: COLORS.grayDark, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const st = STATUS_MAP[e.status] || { label: e.status, color: COLORS.grayDark };
                      return (
                        <tr key={e.id} style={{ borderBottom: "1px solid #f5f5f5", background: i % 2 === 0 ? COLORS.grayLightest : COLORS.white }}>
                          <td style={{ padding: "8px 12px", fontWeight: 700 }}>{e.symbol}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <Chip label={RISK_LABELS[e.riskLevel] || e.riskLevel} size="small"
                              sx={{ bgcolor: RISK_COLORS[e.riskLevel] || COLORS.grayDark, color: COLORS.white, fontWeight: 700 }} />
                          </td>
                          <td style={{ padding: "8px 12px" }}>{FREQ_LABELS[e.frequency] || e.frequency}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <Chip label={st.label} size="small" sx={{ bgcolor: st.color, color: COLORS.white, fontWeight: 700 }} />
                          </td>
                          <td style={{ padding: "8px 12px" }}><KpiBadge kpi={e.trainKpi} /></td>
                          <td style={{ padding: "8px 12px" }}><KpiBadge kpi={e.failOosKpi} /></td>
                          <td style={{ padding: "8px 12px", maxWidth: 200, color: COLORS.grayDark, fontSize: 12 }}>{e.failReason || "—"}</td>
                          <td style={{ padding: "8px 12px", fontSize: 12 }}>
                            {e.lastReviewDate ? (
                              <>
                                <div>{fmtDate(e.lastReviewDate)}</div>
                                <KpiBadge kpi={e.lastReviewKpi} />
                              </>
                            ) : "—"}
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "center" }}>{e.reviewCount}</td>
                          <td style={{ padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(e.createdAt)}</td>
                          <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                            {e.status === "WAITING" && (
                              <>
                                <Button size="small" variant="contained" color="primary"
                                  disabled={!!reviewing[e.id]}
                                  onClick={() => handleReview(e.id)}
                                  sx={{ mr: 1, fontSize: 11, minWidth: 0 }}
                                >
                                  {reviewing[e.id] ? <CircularProgress size={14} color="inherit" /> : "重評"}
                                </Button>
                                <Button size="small" variant="outlined" color="error"
                                  disabled={!!retiring[e.id]}
                                  onClick={() => handleRetire(e.id)}
                                  sx={{ fontSize: 11, minWidth: 0 }}
                                >
                                  退役
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
